import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import { DropdownButton } from "react-bootstrap";
import TablePagination from "./Pagination/Pagination";
import DropdownItem from "react-bootstrap/esm/DropdownItem";
import { useAsyncDebounce, useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";
import { AngleDownIcon, SearchIcon } from "./icons";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";

function ReactTable({
  title,
  columns,
  data,
  onRowClicked,
  activeId,
  moreFilter,
  buttons,
  pagesize = 20,
  size,
  pagination = true,
  filter = true,
  compact = false,
  manualPagination = false,
  manualSortBy = false,
  onSortChange,
  pageCount: controlledPageCount,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  getRowProps = () => ({}),
}) {
  let pageindex = 0;
  let [pageS, setPageS] = useState(pagesize);

  // Only auto-navigate to selected item's page if no filter is active (for client-side pagination only)
  if (!manualPagination) {
    const index = data.findIndex((entry) => entry.id === +activeId);
    if (index > 0) {
      pageindex = parseInt(index / pageS);
    }
  }

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageSize: pagesize, pageIndex: pageindex },
      manualPagination,
      manualSortBy,
      pageCount: controlledPageCount,
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    setPageSize,
    previousPage,
    state: { pageSize, globalFilter, pageIndex, sortBy },
    preGlobalFilteredRows,
    setGlobalFilter,
  } = tableInstance;

  const hasToolbar = Boolean(title) || filter || moreFilter || buttons;
  const wrapperRef = useRef(null);
  const toolbarRef = useRef(null);
  const [toolbarWidth, setToolbarWidth] = useState(0);
  const [availableHeight, setAvailableHeight] = useState(null);

  useLayoutEffect(() => {
    if (!toolbarRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setToolbarWidth(entry.contentRect.width);
    });
    ro.observe(toolbarRef.current);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (compact) return;
    if (!wrapperRef.current) return;

    const recalc = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const next = Math.floor(window.innerHeight - rect.top - 8);
      if (Number.isFinite(next) && next > 220) {
        setAvailableHeight(next);
      }
    };

    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [compact, toolbarWidth, data.length, hasToolbar]);

  const isNarrow = toolbarWidth > 0 && toolbarWidth < 950;

  // Track previous pageSize to only notify on actual changes
  const prevPageSizeRef = React.useRef(pageSize);

  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortBy);
    }
  }, [sortBy, onSortChange]);

  useEffect(() => {
    setPageS(pageSize);

    // Only call onPageSizeChange if pageSize actually changed (not just callback reference)
    if (manualPagination && onPageSizeChange && pageSize !== prevPageSizeRef.current) {
      onPageSizeChange(pageSize);
    }

    prevPageSizeRef.current = pageSize;
  }, [pageSize, manualPagination, onPageSizeChange]);

  // Removed gotoPage(0) effects that were causing pagination resets

  // For manual pagination: sync ONLY on mount and when parent explicitly changes currentPage
  // Track if this is the first render to set initial page
  const isInitialMount = React.useRef(true);
  const prevCurrentPageRef = React.useRef(currentPage);

  useEffect(() => {
    if (!manualPagination) return;

    const targetIndex = currentPage - 1;

    // Only sync if:
    // 1. Initial mount (to set correct starting page), OR
    // 2. Parent explicitly changed currentPage (and it's different from what we just notified)
    if (isInitialMount.current) {
      if (pageIndex !== targetIndex) {
        gotoPage(targetIndex);
      }
      isInitialMount.current = false;
    } else if (currentPage !== prevCurrentPageRef.current && pageIndex !== targetIndex) {
      // Parent changed currentPage (not from our onPageChange notification)
      gotoPage(targetIndex);
    }

    prevCurrentPageRef.current = currentPage;
  }, [currentPage]);

  // Handle manual pagination page changes (notify parent when user clicks pagination)
  // Use a ref to track the last known external currentPage to detect internal changes
  const lastCurrentPageRef = React.useRef(currentPage);

  useEffect(() => {
    if (manualPagination && onPageChange) {
      const expectedIndex = (lastCurrentPageRef.current || 1) - 1;
      // Only notify if pageIndex changed due to user interaction (not external prop sync)
      if (pageIndex !== expectedIndex && currentPage === lastCurrentPageRef.current) {
        onPageChange(pageIndex + 1);
      }
      lastCurrentPageRef.current = currentPage;
    }
  }, [pageIndex, currentPage, manualPagination, onPageChange]);

  return (
    <div
      ref={wrapperRef}
      className={`table-wrapper ${compact ? "table-wrapper--compact" : ""}`}
      style={!compact && availableHeight ? { height: `${availableHeight}px` } : undefined}
    >
      {hasToolbar && (
        <div
          ref={toolbarRef}
          className="table-wrapper__filter d-flex align-items-start gap-2 flex-nowrap"
        >
          {title && (
            <div className="me-3 flex-shrink-0 d-flex align-items-center" style={{ height: 38 }}>
              {typeof title === "string" ? <h5 className="mb-0">{title}</h5> : title}
            </div>
          )}

          <div
            className="d-flex flex-wrap align-items-center gap-2 flex-grow-1"
            style={{ minWidth: 0 }}
          >
            {!manualPagination && filter && (
              <div className="flex-shrink-0" style={{ minWidth: 220 }}>
                <GlobalFilter
                  preGlobalFilteredRows={preGlobalFilteredRows}
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                />
              </div>
            )}

            {filter && (
              <DropdownButton
                title={
                  <div className="d-flex gap-2" title="Page size">
                    <span>{pageSize}</span>
                    <AngleDownIcon />
                  </div>
                }
                variant="btn-transparent"
              >
                {size &&
                  size.map((ps, i) => (
                    <DropdownItem
                      key={i}
                      className="d-flex align-items-center gap-2 px-2 py-1"
                      onClick={() => setPageSize(ps)}
                    >
                      {ps}
                    </DropdownItem>
                  ))}
              </DropdownButton>
            )}

            {moreFilter}
          </div>

          {buttons && (
            <div className="ms-auto d-flex align-items-center gap-2 flex-nowrap flex-shrink-0 align-self-start">
              {typeof buttons === "function" ? buttons({ isNarrow, toolbarWidth }) : buttons}
            </div>
          )}
        </div>
      )}

      <div className="table-wrapper__container">
        <div className="table-wrapper__container__inner" style={{ width: "100%", minWidth: 0 }}>
          <div className="table-wrapper__table-scroll">
            <table {...getTableProps()}>
              <thead>
                {
                  // Loop over the header rows
                  headerGroups.map((headerGroup, i) => (
                    // Apply the header row props
                    <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                      {
                        // Loop over the headers in each row
                        headerGroup.headers.map((column, i) => (
                          // Apply the header cell props
                          <th
                            key={i}
                            {...column.getHeaderProps([
                              column.getSortByToggleProps(),
                              {
                                style: column.style,
                                className: column.className,
                              },
                            ])}
                          >
                            <div className="d-flex align-items-center gap-2 justify-content-between">
                              {column.render("Header")}
                              {
                                <span
                                  className="d-flex align-items-center text-muted"
                                  style={{ fontSize: "0.8em" }}
                                >
                                  {column.disableSortBy ? (
                                    []
                                  ) : column.isSorted ? (
                                    column.isSortedDesc ? (
                                      <FontAwesomeIcon icon={faSortDown} />
                                    ) : (
                                      <FontAwesomeIcon icon={faSortUp} />
                                    )
                                  ) : (
                                    <FontAwesomeIcon icon={faSort} />
                                  )}
                                </span>
                              }
                            </div>
                          </th>
                        ))
                      }
                    </tr>
                  ))
                }
              </thead>
              {/* Apply the table body props */}
              <tbody {...getTableBodyProps()}>
                {isLoading ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-4">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // Loop over the table rows
                  page.map((row, i) => {
                    prepareRow(row);
                    return (
                      // Apply the row props
                      <tr
                        className={+activeId == row.original.id ? "table-wrapper__active" : ""}
                        key={i}
                        {...row.getRowProps({
                          onClick: () => onRowClicked && onRowClicked(row),
                          style: {
                            ...(onRowClicked ? { cursor: "pointer" } : {}),
                            ...(getRowProps(row).style || {}),
                          },
                          className: getRowProps(row).className,
                        })}
                      >
                        {
                          // Loop over the rows cells
                          row.cells.map((cell, i) => {
                            // Apply the cell props
                            return (
                              <td
                                key={i}
                                {...cell.getCellProps([
                                  {
                                    style: {
                                      cursor: onRowClicked ? "pointer" : "default",
                                      ...cell.column.style,
                                    },
                                  },
                                ])}
                              >
                                {cell.render("Cell")}
                              </td>
                            );
                          })
                        }
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {pagination && (
            <div className="table-wrapper__pagination">
              <TablePagination
                pageIndex={pageIndex}
                pageOptions={pageOptions}
                previousPage={previousPage}
                canPreviousPage={canPreviousPage}
                nextPage={nextPage}
                canNextPage={canNextPage}
                gotoPage={gotoPage}
                pageSize={pageSize}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReactTable;

function GlobalFilter({ preGlobalFilteredRows, globalFilter, setGlobalFilter }) {
  const count = preGlobalFilteredRows.length;
  const { t } = useTranslation();
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  // Sync local value with global filter when it changes externally
  React.useEffect(() => {
    setValue(globalFilter);
  }, [globalFilter]);

  return (
    <div className="table-wrapper__filter__searchbar">
      <input
        className="form-control"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={`${count} ${t("records")}...`}
        style={{ maxWidth: "200px" }}
      />
      <SearchIcon top="0rem" left="0.3rem" width={"30px"} />
    </div>
  );
}
